import { Component, effect, Injector, OnInit, input, output, signal } from '@angular/core';
import moment from 'moment';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ICustomGroupReportRes } from 'src/app/store/reports/state';
import { combineLatest } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

type FrequencyOption = {
  label: string;
  value: 'all' | 'Annually' | 'Quarterly' | 'Monthly';
};

type SelectedReportPayload = {
  id: string | number;
  type: string;
};

type PaginatorEvent = {
  pageIndex: number;
};

type SelectedSubClient = {
  planAcronym?: string;
  specifiedId?: string;
  client?: {
    specifiedId?: string;
  };
};

export type CustomGroupReportFormValue = {
  fromDate: string;
  toDate: string;
  frequency: string;
};

export type CustomGroupReportRequestPayload = {
  offset: number;
  planAcronym: string;
  clientSpecifiedId: string;
  subClientSpecifiedId: string;
  formValue: CustomGroupReportFormValue;
};


@Component({
    selector: 'app-custom-group-report-page',
    templateUrl: './custom-group-report.component.html',
    styleUrls: ['./custom-group-report.component.scss'],
    standalone: false
})
export class CustomGroupReportComponent implements OnInit {
  private readonly initialized = signal(false);

  getCustomGroupReport = output<CustomGroupReportRequestPayload>();
  selectedReport = output<SelectedReportPayload>();
  resetReport = output<void>();
  readonly customGroupError = input('');
  customGroupForm: UntypedFormGroup;
  readonly customGroupReports = input<ICustomGroupReportRes | undefined>(undefined);
  defaultPageSize = 25;
  readonly selectedAdminPlan = input('');
  readonly selectedSubClients = signal<SelectedSubClient[]>([]);
  readonly isFetching = input(false);
  readonly showResults = signal(false);
  frequencyDropDown: FrequencyOption[] = [
    { label: 'All', value: 'all' },
    { label: 'Annually', value: 'Annually' },
    { label: 'Quarterly', value: 'Quarterly' },
    { label: 'Monthly', value: 'Monthly' }
  ];

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly injector: Injector
  ) {
    this.customGroupForm = this.createCustomGroupForm();
    const customGroupFormValue = toSignal(combineLatest([
      this.customGroupForm.controls.fromDate.valueChanges.pipe(startWith(this.customGroupForm.controls.fromDate.value || '')),
      this.customGroupForm.controls.toDate.valueChanges.pipe(startWith(this.customGroupForm.controls.toDate.value || '')),
      this.customGroupForm.controls.frequency.valueChanges.pipe(startWith(this.customGroupForm.controls.frequency.value || '')),
    ]), {
      initialValue: [
        this.customGroupForm.controls.fromDate.value || '',
        this.customGroupForm.controls.toDate.value || '',
        this.customGroupForm.controls.frequency.value || ''
      ],
      injector: this.injector
    });

    effect(() => {
      customGroupFormValue();
      if (!this.initialized()) {
        return;
      }

      this.showResults.set(false);
      this.resetReport.emit();
    }, { injector: this.injector });
  }

  ngOnInit(): void {
    this.initialized.set(true);
  }

  private createCustomGroupForm(): UntypedFormGroup {
    return this.fb.group({
      fromDate: [moment(new Date()).subtract(1, 'year').format('MM/DD/YYYY'), Validators.required],
      toDate: [moment(new Date()).format('MM/DD/YYYY')],
      frequency: ['', Validators.required],
    });
  }

  resetOrClientSelected(_event?: unknown): void {
    this.selectedSubClients.set([]);
    this.showResults.set(false);
    this.resetCustomGroupForm();
  }

  subClientSelected(clientInfo: SelectedSubClient[] | null | undefined): void {
    this.selectedSubClients.set(clientInfo || []);
    this.showResults.set(false);
    this.resetCustomGroupForm();
  }

  resetCustomGroupForm(): void {
    this.customGroupForm.reset({
      fromDate: moment(new Date()).subtract(1, 'year').format('MM/DD/YYYY'),
      toDate: moment(new Date()).format('MM/DD/YYYY'),
      frequency: '',
    });
  }

  onCustomGroupSubmitted(pageIndex: number): void {
    const selectedSubClient = this.selectedSubClients()[0];
    const planAcronym = `${selectedSubClient?.planAcronym ?? ''}`.trim();
    const clientSpecifiedId = `${selectedSubClient?.client?.specifiedId ?? ''}`.trim();
    const subClientSpecifiedId = `${selectedSubClient?.specifiedId ?? ''}`.trim();

    if (!planAcronym || !clientSpecifiedId || !subClientSpecifiedId || !this.customGroupForm.valid) {
      return;
    }

    this.showResults.set(true);
    this.getCustomGroupReport.emit({
      offset: pageIndex,
      planAcronym,
      clientSpecifiedId,
      subClientSpecifiedId,
      formValue: {
        ...this.customGroupForm.getRawValue(),
        fromDate: moment(this.customGroupForm.value.fromDate).format('YYYY-MM-DD'),
        toDate: moment(this.customGroupForm.value.toDate).format('YYYY-MM-DD')
      } as CustomGroupReportFormValue
    });
  }

  pageChanged(page: PaginatorEvent): void {
    this.onCustomGroupSubmitted(page.pageIndex >= 1 ? (page.pageIndex * this.defaultPageSize) : 0);
  }

}
